import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Toaster, toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  FileText,
  Users,
  Building2,
  Settings,
  LogOut,
  Menu,
  Home,
  ChevronRight,
  Plus,
  Upload,
  Calendar,
  Phone,
  X,
  Eye,
  Sparkles
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sidebar Component (reused)
const SidebarNav = ({ isOpen, setIsOpen }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();

  const navItems = [
    { icon: LayoutDashboard, label: 'Overview', path: '/dashboard' },
    { icon: Sparkles, label: 'AI Estimate', path: '/dashboard/ai-estimate', isNew: true },
    { icon: FileText, label: 'Cost Estimates', path: '/dashboard/estimates' },
    { icon: Users, label: 'Referrals', path: '/dashboard/referrals' },
    { icon: Building2, label: 'Business Network', path: '/dashboard/businesses' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsOpen(false)} />
      )}

      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-100 transform transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">M</span>
              </div>
              <div>
                <span className="font-semibold text-primary text-sm">MED CONNECT</span>
                <span className="text-accent font-light ml-1 text-sm">ALBANIA</span>
              </div>
            </Link>
          </div>

          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <span className="text-accent font-semibold">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Menu</p>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path) ? 'bg-accent/10 text-accent' : 'text-muted-foreground hover:bg-slate-50 hover:text-primary'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                {item.isNew && <Badge className="ml-auto bg-accent text-white text-xs">NEW</Badge>}
              </Link>
            ))}
            {isAdmin && (
              <div className="pt-4 mt-4 border-t border-slate-100">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Admin</p>
                <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-slate-50 hover:text-primary">
                  <Settings className="w-5 h-5" />Admin Panel
                </Link>
              </div>
            )}
          </nav>

          <div className="p-4 border-t border-slate-100 space-y-2">
            <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-slate-50 hover:text-primary">
              <Home className="w-5 h-5" />Back to Home
            </Link>
            <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 w-full">
              <LogOut className="w-5 h-5" />Sign Out
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Estimate Form Modal
const EstimateFormModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    treatment_type: '',
    description: '',
    preferred_dates: '',
    contact_info: ''
  });
  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const data = new FormData();
      data.append('treatment_type', formData.treatment_type);
      data.append('description', formData.description);
      data.append('preferred_dates', formData.preferred_dates);
      data.append('contact_info', formData.contact_info);
      photos.forEach((photo) => {
        data.append('photos', photo);
      });

      const response = await fetch(`${API_URL}/api/estimates`, {
        method: 'POST',
        credentials: 'include',
        body: data
      });

      if (!response.ok) throw new Error('Failed to submit estimate');

      toast.success('Estimate request submitted successfully!');
      onSuccess();
      onClose();
      setFormData({ treatment_type: '', description: '', preferred_dates: '', contact_info: '' });
      setPhotos([]);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit estimate';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setPhotos(prev => [...prev, ...files]);
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Request Cost Estimate</DialogTitle>
          <DialogDescription>Fill in the details below to receive a free estimate.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Treatment Type</Label>
            <Select value={formData.treatment_type} onValueChange={(v) => setFormData({...formData, treatment_type: v})}>
              <SelectTrigger data-testid="treatment-type-select">
                <SelectValue placeholder="Select treatment type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dental_implants">Dental Implants</SelectItem>
                <SelectItem value="all_on_4">All-on-4</SelectItem>
                <SelectItem value="veneers">Veneers</SelectItem>
                <SelectItem value="smile_makeover">Smile Makeover</SelectItem>
                <SelectItem value="hair_fue">Hair Transplant (FUE)</SelectItem>
                <SelectItem value="hair_dhi">Hair Transplant (DHI)</SelectItem>
                <SelectItem value="botox">Botox</SelectItem>
                <SelectItem value="fillers">Dermal Fillers</SelectItem>
                <SelectItem value="facial">Facial Treatments</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Describe your treatment needs, current condition, and any questions..."
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={4}
              required
              data-testid="estimate-description"
            />
          </div>

          <div className="space-y-2">
            <Label>Upload Photos (Optional)</Label>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-accent transition-colors">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="photo-upload"
              />
              <label htmlFor="photo-upload" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Click to upload medical photos</p>
              </label>
            </div>
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {photos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <img src={URL.createObjectURL(photo)} alt="" className="w-16 h-16 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Preferred Dates</Label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="e.g., March 2025, Flexible"
                value={formData.preferred_dates}
                onChange={(e) => setFormData({...formData, preferred_dates: e.target.value})}
                className="pl-10"
                data-testid="estimate-dates"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Contact Information</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Phone or WhatsApp number"
                value={formData.contact_info}
                onChange={(e) => setFormData({...formData, contact_info: e.target.value})}
                className="pl-10"
                required
                data-testid="estimate-contact"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting} data-testid="submit-estimate-btn">
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Estimates Page Content
const EstimatesContent = () => {
  const [estimates, setEstimates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);

  const fetchEstimates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/estimates`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEstimates(data);
      }
    } catch (error) {
      console.error('Failed to fetch estimates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchEstimates(); }, []);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      reviewed: 'bg-blue-100 text-blue-700',
      quoted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  const getTreatmentLabel = (type) => {
    const labels = {
      dental_implants: 'Dental Implants',
      all_on_4: 'All-on-4',
      veneers: 'Veneers',
      smile_makeover: 'Smile Makeover',
      hair_fue: 'Hair Transplant (FUE)',
      hair_dhi: 'Hair Transplant (DHI)',
      botox: 'Botox',
      fillers: 'Dermal Fillers',
      facial: 'Facial Treatments',
      other: 'Other'
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6" data-testid="estimates-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-primary">Cost Estimates</h1>
          <p className="text-muted-foreground">Request and track your treatment estimates.</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="rounded-full" data-testid="new-estimate-btn">
          <Plus className="w-4 h-4 mr-2" />
          Request Estimate
        </Button>
      </div>

      <EstimateFormModal isOpen={showForm} onClose={() => setShowForm(false)} onSuccess={fetchEstimates} />

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : estimates.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">No estimates yet</h3>
            <p className="text-muted-foreground mb-4">Request your first cost estimate to get started.</p>
            <Button onClick={() => setShowForm(true)} data-testid="first-estimate-btn">
              <Plus className="w-4 h-4 mr-2" /> Request Estimate
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {estimates.map((estimate) => (
            <motion.div
              key={estimate.estimate_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-slate-100 shadow-sm p-6"
            >
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-primary">{getTreatmentLabel(estimate.treatment_type)}</h3>
                    {getStatusBadge(estimate.status)}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{estimate.description}</p>
                  <p className="text-xs text-muted-foreground">
                    Submitted: {new Date(estimate.created_at).toLocaleDateString()}
                  </p>
                  {estimate.estimated_price && (
                    <p className="text-lg font-bold text-accent mt-2">€{estimate.estimated_price}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" onClick={() => setSelectedEstimate(estimate)}>
                        <Eye className="w-4 h-4 mr-2" /> View Details
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{getTreatmentLabel(estimate.treatment_type)}</DialogTitle>
                        <DialogDescription>Estimate Details</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-muted-foreground">Description</Label>
                          <p className="mt-1">{estimate.description}</p>
                        </div>
                        {estimate.preferred_dates && (
                          <div>
                            <Label className="text-muted-foreground">Preferred Dates</Label>
                            <p className="mt-1">{estimate.preferred_dates}</p>
                          </div>
                        )}
                        <div>
                          <Label className="text-muted-foreground">Status</Label>
                          <div className="mt-1">{getStatusBadge(estimate.status)}</div>
                        </div>
                        {estimate.admin_response && (
                          <div>
                            <Label className="text-muted-foreground">Response</Label>
                            <p className="mt-1 p-3 bg-slate-50 rounded-lg">{estimate.admin_response}</p>
                          </div>
                        )}
                        {estimate.estimated_price && (
                          <div>
                            <Label className="text-muted-foreground">Estimated Price</Label>
                            <p className="mt-1 text-2xl font-bold text-accent">€{estimate.estimated_price}</p>
                          </div>
                        )}
                        {estimate.photos?.length > 0 && (
                          <div>
                            <Label className="text-muted-foreground">Attached Photos</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {estimate.photos.map((photo, i) => (
                                <img key={i} src={photo} alt="" className="w-20 h-20 object-cover rounded-lg" />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Estimates Page
export default function EstimatesPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-center" richColors />
      <SidebarNav isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      <header className="sticky top-0 z-30 bg-white border-b border-slate-100 lg:pl-64">
        <div className="flex items-center justify-between px-4 md:px-8 h-16">
          <div className="flex items-center gap-4">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2 text-sm">
              <Link to="/dashboard" className="text-muted-foreground hover:text-primary">Dashboard</Link>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">Cost Estimates</span>
            </div>
          </div>
        </div>
      </header>
      
      <main className="lg:pl-64 pt-6 pb-12 px-4 md:px-8">
        <EstimatesContent />
      </main>
    </div>
  );
}
